/**
 * 스킬 카탈로그용 한글 라벨/설명 맵 — **v2 재편 (2026-04-23)**.
 *
 * <h3>v2 핵심 변경</h3>
 * <ul>
 *   <li>신 3축: <strong>훈련 (~의 서)</strong> / <strong>연구 (~의 혼)</strong> / <strong>각인</strong> 으로 재편.
 *     기존 학파·역할·피해 속성 taxonomy 는 호환용으로 유지.
 *   </li>
 *   <li>확장성 원칙: 하드코드 금지 · id vs 표시 분리 · rider 느슨한 결합.</li>
 *   <li>모든 수치 (마스터 24회, GCD 4턴, 각인 확률 등) 는 {@link COMBAT_CONFIG} 에 집중.</li>
 * </ul>
 *
 * <h3>설계 원칙</h3>
 * <ol>
 *   <li>내부 id 는 영문 고정 (`seo.seom-gwang`). 저장된 런 데이터는 id 참조 — 이름 바뀌어도 마이그레이션 불필요.</li>
 *   <li>한글 라벨은 간결 (2~6자). 리스트/배지에서 짧게 읽혀야 함.</li>
 *   <li>description 은 툴팁용 1~2문장. "이 태그가 전투에서 뭘 의미하는가" 중심.</li>
 *   <li>엔진에 실제로 연동된 effectKind 만 `wired: true`. 미연동은 admin 에서 경고 배지.</li>
 * </ol>
 *
 * <p>상세 설계: {@link file://../../../docs/engine/combat-redesign-260423.md}
 */

// ═════════════════════════════════════════════════════════════════════════════
// 신규 축 (v2)
// ═════════════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────────────────
// 훈련 (서) — 물리 스킬 그룹 · MVP 6계
// ─────────────────────────────────────────────────────────────────────────────

export interface SeoMeta {
    /** 영문 id (저장 데이터 참조). 이름 변경과 무관한 안정 키. */
    id: string;
    /** 한글 2글자 이름. 플레이어 표시용. */
    label: string;
    /** 정체성 한 줄. */
    tagline: string;
    /** 특화 스탯 (마스터 시 보너스 기준). */
    specializedStats: string[];
    /** 계열 연출 색. */
    accentToken: "ember" | "brass" | "steel" | "moss" | "burgundy" | "parchment";
}

export const SEO_META: Record<string, SeoMeta> = {
    "seo.gyeoldan":   { id: "seo.gyeoldan",   label: "결단", tagline: "한 방으로 결정하는 결.",        specializedStats: ["str"],        accentToken: "ember" },
    "seo.seomgwang":  { id: "seo.seomgwang",  label: "섬광", tagline: "찰나의 섬광으로 빠져 나오는 결.", specializedStats: ["spd", "tec"], accentToken: "brass" },
    "seo.suho":       { id: "seo.suho",       label: "수호", tagline: "단단히 지키고 받아치는 결.",      specializedStats: ["vit", "def"], accentToken: "steel" },
    "seo.chugyeok":   { id: "seo.chugyeok",   label: "추격", tagline: "끝까지 쫓아 압박하는 결.",        specializedStats: ["spd", "str"], accentToken: "moss" },
    "seo.bulmyeol":   { id: "seo.bulmyeol",   label: "불멸", tagline: "무너지지 않고 버티는 결.",        specializedStats: ["vit", "spi"], accentToken: "parchment" },
    "seo.seungbu":    { id: "seo.seungbu",    label: "승부", tagline: "한 판에 모든 걸 거는 결.",        specializedStats: ["str"],        accentToken: "burgundy" },
};

// ─────────────────────────────────────────────────────────────────────────────
// 연구 (혼) — 마법 스킬 그룹 · MVP 5계
// ─────────────────────────────────────────────────────────────────────────────
// **상성 없음**. 속성은 연출·공격 패턴 차이만.

export interface HonMeta {
    id: string;
    label: string;
    tagline: string;
    specializedStats: string[];
    accentToken: "ember" | "brass" | "steel" | "moss" | "burgundy" | "parchment";
}

export const HON_META: Record<string, HonMeta> = {
    "hon.hwayeom":  { id: "hon.hwayeom",  label: "화염", tagline: "폭발과 지속 화염의 혼.",    specializedStats: ["int"],        accentToken: "ember" },
    "hon.seori":    { id: "hon.seori",    label: "서리", tagline: "둔화와 결빙의 혼.",          specializedStats: ["int", "spi"], accentToken: "steel" },
    "hon.eodeum":   { id: "hon.eodeum",   label: "어둠", tagline: "저주와 생명 흡수의 혼.",     specializedStats: ["int"],        accentToken: "burgundy" },
    "hon.baram":    { id: "hon.baram",    label: "바람", tagline: "이동과 음파의 혼.",          specializedStats: ["spd", "int"], accentToken: "moss" },
    "hon.byeolbit": { id: "hon.byeolbit", label: "별빛", tagline: "성가와 정화의 혼.",          specializedStats: ["spi"],        accentToken: "parchment" },
};

// ─────────────────────────────────────────────────────────────────────────────
// 각인 — 무기 rider · 11종 (순 우리말)
// ─────────────────────────────────────────────────────────────────────────────
//
// 각인은 무기에 부여되는 rider. 상성 없음. 어떤 스킬이든 그 무기로 발동 시 rider 덧씌움.
// 밸런스는 추후 조정 — rider 효과는 "명세" 수준이라 자유롭게 바뀔 수 있음 (§9 확장성).

export interface EngraveMeta {
    id: string;
    label: string;
    meaning: string;
    /** rider 효과 서술 (플레이어 툴팁용). 엔진 로직과 느슨하게 결합. */
    rider: string;
    /** 계열 연출 색. */
    accentToken: "ember" | "brass" | "steel" | "moss" | "burgundy" | "parchment";
    /** rider 효과 상시성 — 확률 trigger 인가, 매 타격 고정 효과인가. */
    triggerMode: "probabilistic" | "always" | "conditional";
}

export const ENGRAVE_META: Record<string, EngraveMeta> = {
    "engrave.noeul":    { id: "engrave.noeul",    label: "노을",    meaning: "저녁빛",          rider: "10% 확률 폭발 (주변 반경 추가 피해 50%)",      accentToken: "ember",     triggerMode: "probabilistic" },
    "engrave.narae":    { id: "engrave.narae",    label: "나래",    meaning: "날개",            rider: "타격 시 15% 확률 본인 1턴 가속 (글로벌 쿨타임 절반)", accentToken: "moss",      triggerMode: "probabilistic" },
    "engrave.yeoul":    { id: "engrave.yeoul",    label: "여울",    meaning: "얕은 물 흐름",    rider: "20% 확률 적 둔화 (spd -3, 1턴)",               accentToken: "steel",     triggerMode: "probabilistic" },
    "engrave.eoseureum": { id: "engrave.eoseureum", label: "어스름", meaning: "저녁 어둠",       rider: "10% 확률 적 명중률 -15% (2턴)",                accentToken: "burgundy",  triggerMode: "probabilistic" },
    "engrave.meari":    { id: "engrave.meari",    label: "메아리",  meaning: "반향",            rider: "15% 확률 같은 타격 재발동 (원 피해의 30%)",    accentToken: "brass",     triggerMode: "probabilistic" },
    "engrave.yunseul":  { id: "engrave.yunseul",  label: "윤슬",    meaning: "물 반짝임",       rider: "치명 확률 +5% 상시",                            accentToken: "parchment", triggerMode: "always" },
    "engrave.miri-nae": { id: "engrave.miri-nae", label: "미리내",  meaning: "은하수",          rider: "치명타가 터지면 마력 +3",                        accentToken: "steel",     triggerMode: "conditional" },
    "engrave.byeolbit": { id: "engrave.byeolbit", label: "별빛",    meaning: "별의 빛",         rider: "타격 시 적의 공격력 -10% (2턴)",                accentToken: "parchment", triggerMode: "always" },
    "engrave.dalbit":   { id: "engrave.dalbit",   label: "달빛",    meaning: "달의 빛",         rider: "내 체력이 절반 아래면 타격 피해 +20%",          accentToken: "brass",     triggerMode: "conditional" },
    "engrave.saebyeok": { id: "engrave.saebyeok", label: "새벽",    meaning: "아침 빛",         rider: "적을 쓰러뜨리면 방어력 +5 (3턴)",               accentToken: "moss",      triggerMode: "conditional" },
    "engrave.haneul":   { id: "engrave.haneul",   label: "하늘",    meaning: "무속성 / 균형",   rider: "덧새김은 없지만 기본 수치 +5%, 착용 조건 완화", accentToken: "parchment", triggerMode: "always" },
};

// ─────────────────────────────────────────────────────────────────────────────
// 무기 프로필 — 7 무기
// ─────────────────────────────────────────────────────────────────────────────
//
// 스킬 계열 (서/혼) 과 독립적. 지팡이만 유일하게 마법 경제 (주문 증폭 + MP 회수).

export interface WeaponProfile {
    id: string;
    label: string;
    speedMul: number;
    damageMul: number;
    /** 치명 확률 가산 (+0.15 = +15%). */
    critBonus: number;
    /** 명중률 가산. */
    accuracyBonus: number;
    range: number;
    costModel: "physical" | "magical";
    /** 특수 효과 서술 (툴팁용). */
    specialTraits: string[];
    /** 착용 조건. */
    statRequirements: Partial<Record<"str" | "tec" | "int" | "spi" | "vit", number>>;
    /** UI 표시용 카테고리 그룹핑. */
    category: WeaponCategory;
    /** 무기 스프라이트 키 (전투 레이어). */
    spriteId: string;
}

export type WeaponCategory =
    | "melee-blade"    // 단검·장검·도끼
    | "melee-polearm"  // 창
    | "ranged-bow"     // 활
    | "ranged-gun"     // 총
    | "caster-staff"   // 지팡이
    | "unarmed";       // 무기 없이

export const WEAPON_META: Record<string, WeaponProfile> = {
    "weapon.dagger": {
        id: "weapon.dagger", label: "단검",
        speedMul: 1.4, damageMul: 0.7, critBonus: 0.15, accuracyBonus: 0, range: 1.2,
        costModel: "physical", category: "melee-blade", spriteId: "dagger",
        specialTraits: ["완력 요구가 낮고, 연타에 잘 맞음"],
        statRequirements: {},
    },
    "weapon.sword": {
        id: "weapon.sword", label: "장검",
        speedMul: 1.0, damageMul: 1.0, critBonus: 0, accuracyBonus: 0.10, range: 1.5,
        costModel: "physical", category: "melee-blade", spriteId: "sword",
        specialTraits: ["균형형"],
        statRequirements: { str: 8 },
    },
    "weapon.axe": {
        id: "weapon.axe", label: "도끼",
        speedMul: 0.7, damageMul: 1.6, critBonus: 0.20, accuracyBonus: -0.10, range: 1.5,
        costModel: "physical", category: "melee-blade", spriteId: "axe",
        specialTraits: ["뽕맛 — 한 방 크리"],
        statRequirements: { str: 14 },
    },
    "weapon.spear": {
        id: "weapon.spear", label: "창",
        speedMul: 1.0, damageMul: 1.0, critBonus: 0, accuracyBonus: 0.05, range: 2.5,
        costModel: "physical", category: "melee-polearm", spriteId: "spear",
        specialTraits: ["타격 시 적 0.5 밀침", "연장 찌르기 (2차 0.3x)"],
        statRequirements: { str: 10, tec: 6 },
    },
    "weapon.staff": {
        id: "weapon.staff", label: "지팡이",
        speedMul: 1.0, damageMul: 0.5, critBonus: 0, accuracyBonus: 0, range: 1.8,
        costModel: "magical", category: "caster-staff", spriteId: "staff",
        specialTraits: ["주문 위력 1.3배", "평타 시 마력 +1"],
        statRequirements: { int: 10 },
    },
    "weapon.bow": {
        id: "weapon.bow", label: "활",
        speedMul: 1.0, damageMul: 0.9, critBonus: 0, accuracyBonus: 0, range: 5.0,
        costModel: "physical", category: "ranged-bow", spriteId: "bow",
        specialTraits: ["근접(거리<1.5) 시 명중 -20%"],
        statRequirements: { tec: 8 },
    },
    "weapon.gun": {
        id: "weapon.gun", label: "마도총",
        speedMul: 0.5, damageMul: 1.4, critBonus: 0, accuracyBonus: 0, range: 4.5,
        costModel: "physical", category: "ranged-gun", spriteId: "gun",
        specialTraits: ["5% 확률로 고장, 다음 턴 재장전", "기술 요구가 큼"],
        statRequirements: { tec: 14 },
    },
};

export const WEAPON_CATEGORY_LABEL: Record<WeaponCategory, string> = {
    "melee-blade":    "근접 · 검류",
    "melee-polearm":  "근접 · 장병",
    "ranged-bow":     "원거리 · 활",
    "ranged-gun":     "원거리 · 총",
    "caster-staff":   "시전 · 지팡이",
    "unarmed":        "무기 없이",
};

// ─────────────────────────────────────────────────────────────────────────────
// 쓰임 — 6 대분류 (필터링 축)
// ─────────────────────────────────────────────────────────────────────────────

export type UsageCategory = "offense" | "control" | "support" | "defense" | "mobility" | "summon";

export interface UsageMeta {
    id: UsageCategory;
    label: string;
    description: string;
    accentToken: "ember" | "brass" | "steel" | "moss" | "burgundy" | "parchment";
}

export const USAGE_META: Record<UsageCategory, UsageMeta> = {
    offense:  { id: "offense",  label: "공격", description: "적에게 피해.",             accentToken: "ember" },
    control:  { id: "control",  label: "제어", description: "적 행동을 제약.",           accentToken: "brass" },
    support:  { id: "support",  label: "지원", description: "아군 강화 / 적 약화.",       accentToken: "moss" },
    defense:  { id: "defense",  label: "방어", description: "피해 감소 / 무효화.",         accentToken: "steel" },
    mobility: { id: "mobility", label: "기동", description: "위치 이동.",                 accentToken: "brass" },
    summon:   { id: "summon",   label: "소환", description: "보조 존재를 불러 전장을 거드는 갈래.", accentToken: "burgundy" },
};

// ─────────────────────────────────────────────────────────────────────────────
// 피해 결 — 3 그룹 (UI 최소 표시)
// ─────────────────────────────────────────────────────────────────────────────

export type DamageGroup = "physical" | "magic" | "element";

export const DAMAGE_GROUP_META: Record<DamageGroup, { label: string; description: string }> = {
    physical: { label: "물리", description: "근접 타격·관통·참격." },
    magic:    { label: "마법", description: "주문·저주·정신 충격." },
    element:  { label: "원소", description: "불·서리·번개·음파 등 자연력." },
};

/** 기존 13 속성 → 3 그룹 매핑. */
export const DAMAGE_TYPE_TO_GROUP: Record<string, DamageGroup> = {
    physical:  "physical",
    magic:     "magic",
    magical:   "magic",
    arcane:    "magic",
    hex:       "magic",
    holy:      "magic",
    cold:      "element",
    frost:     "element",
    heat:      "element",
    lightning: "element",
    water:     "element",
    nature:    "element",
    sonic:     "element",
};

// ═════════════════════════════════════════════════════════════════════════════
// 레벨 시스템 (공통)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * 서/혼 레벨 곡선. 인덱스 = Lv-1. 값 = 그 Lv 도달까지 **누적 필요 회수**.
 *
 * Lv 1 = 0 (선택 시 즉시 1Lv)
 * Lv 2 = 3 (훈련/연구 3회)
 * Lv 3 = 8
 * Lv 4 = 15
 * Lv 5 = 24 (마스터)
 */
export const LEVEL_CURVE_CUMULATIVE: readonly number[] = [0, 3, 8, 15, 24];
export const MASTERY_LEVEL = 5;
export const MASTERY_TOTAL_ACTIONS = 24;

export interface LevelReward {
    /** 해당 Lv 에서 해금하는 스킬 id 슬롯 (서/혼 정의 시 채워짐). */
    unlockSlotIndex: number;
    /** Lv 도달 시 적용되는 수동 효과 서술. */
    description: string;
    /** Lv 3/4 같은 중간 단계의 추가 보너스 (스탯/위력). */
    bonus?: string;
}

export const LEVEL_REWARD_TEMPLATE: readonly LevelReward[] = [
    { unlockSlotIndex: 0, description: "노멀 스킬 A 해금" },
    { unlockSlotIndex: 1, description: "노멀 스킬 B 해금" },
    { unlockSlotIndex: 2, description: "레어 스킬 C 해금", bonus: "기존 스킬 위력 +10%" },
    { unlockSlotIndex: 3, description: "레어 스킬 D 해금", bonus: "계열 특화 스탯 보너스" },
    { unlockSlotIndex: 4, description: "시그니처 E 해금", bonus: "마스터 장비 + 칭호 + 서사 플래그" },
];

// ═════════════════════════════════════════════════════════════════════════════
// 전투 메커니즘 상수 (한 곳에 모음 — 확장성)
// ═════════════════════════════════════════════════════════════════════════════

export const COMBAT_CONFIG = {
    /** 글로벌 쿨다운 기본 턴. */
    gcdBaseTurns: 4,
    /** spd N 마다 GCD -1. */
    gcdSpdReduction: 5,
    /** GCD 바닥. */
    gcdMinTurns: 2,

    /** 시전 속도 배수 공식 계수 (`castMul = 1 / (1 + tec × coef)`). */
    castSpeedTecCoef: 0.04,

    /** 시전 캔슬 기본 확률. */
    castCancelBaseChance: 0.30,
    /** spi N 마다 캔슬 확률 -2%. */
    castCancelSpiReduction: 1,
    castCancelSpiReductionAmount: 0.02,

    /** 총 고장률 기본. */
    gunJamBaseChance: 0.05,
    /** tec 당 고장률 -0.3%. */
    gunJamTecReduction: 0.003,
    /** 고장률 바닥. */
    gunJamMinChance: 0.005,

    /** 지팡이 주문 위력 배수. */
    staffSpellAmplifier: 1.3,

    /** 제련 (대장간) 확률 — tec 10 기준. */
    enchantBaseChances: {
        criticalFailure: 0.05,  // 대실패: 재료 + 장비 소실
        failure: 0.25,           // 실패: 재료 소실
        success: 0.50,
        greatSuccess: 0.15,     // 대성공: 수치 +50%
        perfect: 0.05,           // 완성: 수치 x2 + 슬롯 해제
    },
} as const;

// ═════════════════════════════════════════════════════════════════════════════
// 기존 축 (호환/내부용) — 점진적 마이그레이션
// ═════════════════════════════════════════════════════════════════════════════
//
// 기존 스킬 데이터에 school/role/damageType 필드가 남아 있으므로 호환 유지.
// Admin 뷰는 신 taxonomy 우선, 기존은 "legacy" 섹션으로 노출.

// ─────────────────────────────────────────────────────────────────────────────
// 기존 학파 (8) — legacy flavor tag 로 격하
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 2026-04-24 Phase 30: 레거시 학파(school) 축 완전 폐기.
 *   - SCHOOL_META, SchoolMeta, labelForSchool 전부 제거됨.
 *   - v2 는 서(seo) / 혼(hon) / 각인(engrave) 3축 — SEO_META / HON_META / ENGRAVE_META 참조.
 *   - skillSchoolMap 이 여전히 "팔레트 키" 용도로 쓰이는데, 이는 pseudoSchoolFromRole +
 *     weaponCategoryPaletteKey 가 같은 문자열 도메인(blade/beast/…)으로 값을 내는 것을 재활용
 *     하기 위함. "school" 이라는 이름은 남았지만 의미는 "팔레트 키".
 */

// ─────────────────────────────────────────────────────────────────────────────
// 기존 역할 (37) — 내부 tag · 필터링·검색용으로만 유지
// ─────────────────────────────────────────────────────────────────────────────

export type RoleCategory = "offense" | "control" | "support" | "defense" | "mobility" | "summon" | "reactive" | "utility";

export interface RoleMeta {
    label: string;
    category: RoleCategory;
    description: string;
}

export const ROLE_META: Record<string, RoleMeta> = {
    OFFENSE_DIRECT:      { label: "직격",     category: "offense", description: "단일 대상에 즉발 피해." },
    OFFENSE_BURST:       { label: "연타",     category: "offense", description: "짧은 시간에 여러 번 피해." },
    OFFENSE_AOE:         { label: "광역 공세", category: "offense", description: "원형·부채꼴 범위 피해." },
    OFFENSE_AREA:        { label: "광역 공세", category: "offense", description: "지역 단위의 피해." },
    OFFENSE_LINE:        { label: "일직선",   category: "offense", description: "자신에서 뻗어나가는 선형 피해." },
    OFFENSE_DASH:        { label: "돌격 피해", category: "offense", description: "이동하며 피해." },
    OFFENSE_DOT:         { label: "지속 피해", category: "offense", description: "출혈·독 등." },
    OFFENSE_DELAYED:     { label: "지연 피해", category: "offense", description: "시전 후 발동." },
    OFFENSE_EXECUTE:     { label: "처형",     category: "offense", description: "낮은 체력 대상 가중." },
    OFFENSE_LIFESTEAL:   { label: "흡혈",     category: "offense", description: "피해 일부 자가 회복." },
    OFFENSE_CONTROL:     { label: "공세+제어", category: "offense", description: "피해 + 제어 동시." },
    CONTROL:             { label: "제어",     category: "control", description: "속박/둔화 등 일반." },
    CONTROL_STUN:        { label: "기절",     category: "control", description: "짧은 행동 봉쇄." },
    CONTROL_FREEZE:      { label: "결빙",     category: "control", description: "얼려 행동 정지." },
    CONTROL_FEAR:        { label: "공포",     category: "control", description: "적을 뒤로 밀거나 이탈 유도." },
    CONTROL_PULL:        { label: "끌기",     category: "control", description: "적을 자신 쪽으로 당김." },
    CONTROL_MELEE:       { label: "근접 제어", category: "control", description: "근거리 속박/경직." },
    CONTROL_INTERRUPT:   { label: "시전 차단", category: "control", description: "적 캐스트 차단." },
    CONTROL_AREA:        { label: "광역 제어", category: "control", description: "범위 다수 제어." },
    CONTROL_AREA_DOT:    { label: "광역 장판", category: "control", description: "장판 위 대상에 지속 효과." },
    CONTROL_DISPEL:      { label: "해제",     category: "control", description: "적 버프/장판 제거." },
    SUPPORT_BUFF:        { label: "아군 강화", category: "support", description: "아군/자신 버프." },
    SUPPORT_BUFF_RISKY:  { label: "위험 강화", category: "support", description: "강한 버프 + 반동." },
    SUPPORT_DEBUFF:      { label: "적 약화",  category: "support", description: "적 능력치 감소." },
    SUPPORT_HEAL:        { label: "회복",     category: "support", description: "체력 복구." },
    SUPPORT_HASTE:       { label: "가속",     category: "support", description: "행동 속도 증가." },
    SUPPORT_INVULN:      { label: "무적",     category: "support", description: "짧은 피해 무효." },
    SUPPORT_REFLECT:     { label: "반사",     category: "support", description: "피해/주문 반사." },
    SUPPORT_RECOVER:     { label: "자원 회수", category: "support", description: "정신력·쿨다운 회수." },
    SUPPORT_STEALTH:     { label: "은신",     category: "support", description: "탐지 회피." },
    SUPPORT_SUSTAIN:     { label: "지속 유지", category: "support", description: "장기전 유지." },
    SUPPORT_DISPLACE:    { label: "재배치",   category: "support", description: "아군/자신 위치 이동." },
    DEFENSE:             { label: "방어",     category: "defense",  description: "받는 피해 감소." },
    MOBILITY:            { label: "기동",     category: "mobility", description: "돌진·점멸." },
    SUMMON:              { label: "소환",     category: "summon",   description: "소환수·덫 전개." },
    REACTIVE:            { label: "반응",     category: "reactive", description: "조건 트리거 패시브." },
    UTILITY:             { label: "범용",     category: "utility",  description: "기타 유틸." },
};

// ─────────────────────────────────────────────────────────────────────────────
// 희귀도 / 유형 / 범위 / 사거리 방식
// ─────────────────────────────────────────────────────────────────────────────

export const RARITY_META: Record<string, { label: string; paletteToken: string }> = {
    COMMON:    { label: "일반", paletteToken: "slate" },
    UNCOMMON:  { label: "예사", paletteToken: "moss" },
    RARE:      { label: "드묾", paletteToken: "brass" },
    EPIC:      { label: "귀함", paletteToken: "ember" },
    LEGENDARY: { label: "전설", paletteToken: "burgundy" },
};

export const TYPE_META: Record<string, { label: string; description: string }> = {
    ACTIVE:  { label: "능동", description: "전술 규칙이 집어 들면 해당 턴에 사용." },
    PASSIVE: { label: "항시", description: "장착만 하면 조건부로 자동 적용." },
};

export interface ScopeMeta {
    label: string;
    description: string;
    twoDAware: boolean;
}

export const SCOPE_META: Record<string, ScopeMeta> = {
    SINGLE:      { label: "단일",      description: "한 대상만 타격.",                         twoDAware: false },
    AOE_CIRCLE:  { label: "원형 광역", description: "중심점 기준 원형 범위.",                   twoDAware: true  },
    AOE_CONE:    { label: "부채꼴",    description: "시전자 기준 각도 원뿔형 범위.",            twoDAware: true  },
    AOE_LINE:    { label: "일직선",    description: "자신에서 뻗어나가는 폭 있는 직선.",        twoDAware: true  },
    SELF:        { label: "자신",      description: "자신에게만 적용.",                         twoDAware: false },
    CONTEXTUAL:  { label: "상황 지정", description: "전술/플래그에 따라 대상이 달라짐.",        twoDAware: false },
    GROUND_TRAP: { label: "장판",      description: "바닥에 설치. 밟는 대상에 발동.",           twoDAware: true  },
};

export const RANGE_MODE_META: Record<string, { label: string; description: string }> = {
    WEAPON:    { label: "무기 사거리", description: "장착 무기의 사거리를 그대로 사용." },
    FIXED:     { label: "고정 거리",   description: "스킬 자체 range 값 사용 (무기 무관)." },
    SELF:      { label: "자신 영역",   description: "자신 반경 내." },
    SELF_TILE: { label: "자신 타일",   description: "자신 위치만 대상." },
};

// ─────────────────────────────────────────────────────────────────────────────
// 피해 속성 (구 13종) — 3 그룹 매핑 + 개별 라벨 (flavor 텍스트용)
// ─────────────────────────────────────────────────────────────────────────────

export const DAMAGE_TYPE_META: Record<string, { label: string; group: DamageGroup }> = {
    physical:  { label: "물리",      group: "physical" },
    magic:     { label: "마법",      group: "magic" },
    magical:   { label: "마법",      group: "magic" },
    arcane:    { label: "비의",      group: "magic" },
    hex:       { label: "주문",      group: "magic" },
    cold:      { label: "냉기",      group: "element" },
    frost:     { label: "서리",      group: "element" },
    heat:      { label: "화염",      group: "element" },
    lightning: { label: "번개",      group: "element" },
    water:     { label: "수력",      group: "element" },
    nature:    { label: "자연",      group: "element" },
    sonic:     { label: "음파",      group: "element" },
    holy:      { label: "신성",      group: "magic" },
};

// ─────────────────────────────────────────────────────────────────────────────
// EffectKind — 엔진 연동/미연동 구분 유지
// ─────────────────────────────────────────────────────────────────────────────

export interface EffectKindMeta {
    label: string;
    description: string;
    wired: boolean;
}

export const EFFECT_KIND_META: Record<string, EffectKindMeta> = {
    DAMAGE:                    { label: "피해",             description: "대상에 피해.",                        wired: true  },
    HEAL:                      { label: "회복",             description: "대상 체력 회복.",                     wired: true  },
    BUFF_STAT:                 { label: "능력치 증가",       description: "대상 스탯 일시 상승.",                wired: true  },
    DEBUFF_STAT:               { label: "능력치 감소",       description: "대상 스탯 일시 하락.",                wired: true  },
    APPLY_MARK:                { label: "표식 부여",         description: "대상에 상태 표식 부착.",              wired: true  },
    APPLY_SELF_MARK:           { label: "자신에게 표식",     description: "자신에게 표식 부착.",                 wired: true  },
    SELF_DAMAGE:               { label: "자폭 피해",         description: "자신에게 피해 (반동 스킬).",          wired: true  },
    TELEPORT_TOWARD_TARGET:    { label: "순간이동(대상쪽)",  description: "대상 방향으로 즉시 이동.",            wired: true  },
    TELEPORT_AWAY:             { label: "순간이동(회피)",    description: "대상에서 멀어지게 즉시 이동.",        wired: true  },
    TELEPORT_BEHIND_TARGET:    { label: "순간이동(배후)",    description: "대상 뒤로 이동.",                     wired: true  },
    PULL_TARGET:               { label: "대상 끌기",         description: "대상을 자신 쪽으로 당김.",            wired: true  },
    PUSH_TARGET:               { label: "대상 밀기",         description: "대상을 멀리 밀침.",                   wired: true  },
    BIND_TARGET:               { label: "속박",             description: "대상 행동을 짧게 정지.",              wired: true  },
    SELF_HASTE:                { label: "자신 가속",         description: "자신 행동 속도 증가.",                wired: true  },

    APPLY_AURA_ON_ENEMIES:     { label: "적 광역 오라",      description: "적들에게 지속 오라 적용.",            wired: false },
    BONUS_ON_FIRST_HIT:        { label: "첫 타격 보너스",    description: "전투 첫 피격에 추가 보너스.",          wired: false },
    BUFF_ALL_SUMMONS:          { label: "소환수 전체 버프",  description: "모든 소환수 강화.",                    wired: false },
    CLEANSE:                   { label: "정화",             description: "자신 부정 표식 제거.",                 wired: false },
    CLEANSE_ALLIES:            { label: "아군 정화",         description: "아군 부정 효과 제거.",                 wired: false },
    CONDITIONAL_BONUS:         { label: "조건부 보너스",     description: "조건 충족 시 효과 증폭.",              wired: false },
    CONDITIONAL_BONUS_BY_SCENE_FLAG: { label: "씬 플래그 조건부", description: "특정 씬 플래그 보너스.",         wired: false },
    CONDITIONAL_BONUS_BY_TAG:  { label: "태그 조건부 보너스", description: "대상 태그 기반 보너스.",              wired: false },
    CONDITIONAL_MARK_AMPLIFY:  { label: "표식 증폭",         description: "특정 표식 증폭.",                      wired: false },
    COPY_ENEMY_HIGHEST_STAT_FRACTION: { label: "적 스탯 모방", description: "적 최고 스탯 일부 가져옴.",          wired: false },
    COPY_LAST_ENEMY_SKILL:     { label: "적 스킬 복사",      description: "마지막 적 스킬 모방.",                 wired: false },
    EXTEND_ENEMY_COOLDOWNS:    { label: "적 쿨다운 연장",    description: "적 스킬 쿨다운 증가.",                 wired: false },
    GRANT_FLAG:                { label: "플래그 부여",       description: "씬/런 플래그 설정.",                   wired: false },
    HEAL_ALL_SUMMONS:          { label: "소환수 회복",       description: "모든 소환수 회복.",                    wired: false },
    HEAL_OVER_TIME:            { label: "지속 회복",         description: "여러 턴 체력 회복.",                   wired: false },
    IGNORE_DEFENSE_FRACTION:   { label: "방어 관통",         description: "대상 방어력 일부 무시.",               wired: false },
    MOVE_SELF:                 { label: "자신 이동",         description: "자신을 지정 방향으로 이동.",           wired: false },
    PLACE_TRAP:                { label: "덫 설치",           description: "전장에 트리거 장치 배치.",             wired: false },
    REDIRECT_NEXT_ENEMY_SKILL: { label: "적 스킬 리다이렉트", description: "적 스킬을 다른 대상으로.",           wired: false },
    REDUCE_ALL_SKILL_COOLDOWNS:{ label: "쿨다운 일괄 감소",  description: "자신 모든 스킬 쿨다운 감소.",          wired: false },
    REFLECT_DAMAGE:            { label: "피해 반사",         description: "피해를 되돌림.",                       wired: false },
    REFLECT_SPELL:             { label: "주문 반사",         description: "마법 피해만 반사.",                    wired: false },
    REMOVE_MARK:               { label: "표식 제거",         description: "표식 제거.",                           wired: false },
    RESET_ALL_COOLDOWNS_SELF:  { label: "쿨다운 전체 초기화", description: "자신 모든 스킬 쿨다운 해제.",        wired: false },
    RESET_COOLDOWN_UNUSED_SKILL:{ label: "미사용 스킬 재장전", description: "안 쓴 스킬 쿨다운 초기화.",         wired: false },
    RESTORE_RESOURCE:          { label: "자원 회복",         description: "정신력 등 자원 회복.",                 wired: false },
    REVIVE_ALLIES:             { label: "아군 부활",         description: "쓰러진 아군 부활.",                    wired: false },
    SELF_DEBUFF:               { label: "자신 약화",         description: "자신에게 디버프.",                     wired: false },
    SELF_MARK:                 { label: "자신 표식(별칭)",   description: "APPLY_SELF_MARK 별칭.",                wired: false },
    SHIELD:                    { label: "실드",             description: "피해 흡수 실드.",                      wired: false },
    SUMMON:                    { label: "소환",             description: "소환수 배치.",                         wired: false },
};

// ─────────────────────────────────────────────────────────────────────────────
// Mark (상태 표식) — 내부 tag · 엔진 이벤트 로그용
// ─────────────────────────────────────────────────────────────────────────────

export interface MarkMeta {
    label: string;
    category: "debuff" | "buff" | "terrain" | "narrative";
    description: string;
}

export const MARK_META: Record<string, MarkMeta> = {
    "bleeding":        { label: "출혈",        category: "debuff",    description: "지속 물리 피해." },
    "burn":            { label: "화상",        category: "debuff",    description: "지속 화염 피해." },
    "poison":          { label: "중독",        category: "debuff",    description: "지속 자연 피해." },
    "frozen":          { label: "결빙",        category: "debuff",    description: "행동 정지 + 받는 피해 증가." },
    "rooted":          { label: "뿌리박힘",     category: "debuff",    description: "이동 봉쇄." },
    "silenced":        { label: "침묵",        category: "debuff",    description: "스킬 사용 봉쇄." },
    "slowed":          { label: "둔화",        category: "debuff",    description: "행동 속도 감소." },
    "stagger":         { label: "경직",        category: "debuff",    description: "짧은 행동 중단." },
    "paralyze-short":  { label: "짧은 마비",    category: "debuff",    description: "극단기 행동 봉쇄." },
    "feared":          { label: "공포",        category: "debuff",    description: "뒤로 물러남 / 이탈." },
    "cursed":          { label: "저주",        category: "debuff",    description: "광범위 약화." },
    "curse-stack":     { label: "저주 중첩",    category: "debuff",    description: "저주 스택 증폭." },
    "confused":        { label: "혼란",        category: "debuff",    description: "행동 무작위화." },
    "dispelled":       { label: "해제됨",       category: "debuff",    description: "버프 제거됨." },
    "counterspelled":  { label: "주문 차단",    category: "debuff",    description: "시전 끊김." },
    "fractured":       { label: "균열",        category: "debuff",    description: "방어 약화." },
    "overheat":        { label: "과열",        category: "debuff",    description: "기관술 자기 과열." },
    "hunter-marked":   { label: "사냥 표식",    category: "debuff",    description: "추적자 가중 피해." },
    "resonant-ache":   { label: "공명 통증",    category: "debuff",    description: "표식 공명 피해 증폭." },
    "soaked":          { label: "젖음",        category: "debuff",    description: "번개/냉기 피해 증가." },
    "time-debt":       { label: "시간 빚",      category: "debuff",    description: "이후 행동 느려짐." },
    "initiative-lost": { label: "선공 상실",    category: "debuff",    description: "다음 턴 순서 밀림." },
    "nameless":        { label: "이름 잃음",    category: "debuff",    description: "대상 이름 참조 불가." },
    "divine-shield":   { label: "성스러운 방패", category: "buff",     description: "짧은 무적." },
    "mana-shield":     { label: "정신력 방패",  category: "buff",     description: "피해를 정신력으로." },
    "frost-aura":      { label: "서리 오라",    category: "buff",     description: "주변 적 둔화." },
    "mending-prayer":  { label: "치유 기도",    category: "buff",     description: "지속 회복." },
    "rain-regen":      { label: "비의 재생",    category: "buff",     description: "지속 회복." },
    "thorn-reflect":   { label: "가시 반사",    category: "buff",     description: "근접 피격 반사." },
    "berserk-fatigue": { label: "광분 피로",    category: "buff",     description: "강화 + 종료 후 약화." },
    "star-ember":      { label: "별 불씨",      category: "buff",     description: "상황별 강화." },
    "grapple-dash":    { label: "돌격 훅",      category: "buff",     description: "다음 돌격 강화." },
    "blinked":         { label: "점멸",        category: "buff",     description: "이동 후 짧은 회피 상태." },
    "triple-barrage-2":{ label: "연속 포격 ②", category: "buff",     description: "연속 포격 카운터." },
    "triple-barrage-3":{ label: "연속 포격 ③", category: "buff",     description: "연속 포격 카운터." },
    "immobile-self":   { label: "자발 고정",    category: "buff",     description: "이동 포기 · 다른 효과 강화." },
    "consecrated-ground": { label: "성화된 땅", category: "terrain",   description: "범위 내 아군 보호." },
    "scorched-ground":    { label: "불탄 땅",   category: "terrain",   description: "범위 내 화염 피해." },
    "drift-poison":       { label: "부유 독",   category: "terrain",   description: "바람에 퍼지는 독 장판." },
    "riptide-dot":        { label: "역류 피해", category: "terrain",   description: "조류 휩쓸림." },
    "face-remembered":    { label: "얼굴 기억됨", category: "narrative", description: "서사 플래그." },
    "named-by-mother":    { label: "어머니가 부른 이름", category: "narrative", description: "서사 플래그 (시아신 전용)." },
    "death-timer":        { label: "죽음의 시계", category: "narrative", description: "지속 카운트다운." },
};

// ═════════════════════════════════════════════════════════════════════════════
// 공용 헬퍼
// ═════════════════════════════════════════════════════════════════════════════

/** 서 라벨. 알려지지 않은 id 면 id 그대로. */
export function labelForSeo(id: string): string {
    return SEO_META[id]?.label ?? id;
}
export function labelForHon(id: string): string {
    return HON_META[id]?.label ?? id;
}
export function labelForEngrave(id: string): string {
    return ENGRAVE_META[id]?.label ?? id;
}
export function labelForWeapon(id: string): string {
    return WEAPON_META[id]?.label ?? id;
}
export function labelForUsage(id: UsageCategory): string {
    return USAGE_META[id]?.label ?? id;
}
export function labelForDamageGroup(id: DamageGroup): string {
    return DAMAGE_GROUP_META[id]?.label ?? id;
}

// ── 2026-04-24 Phase 30: labelForSchool 제거. v2 SEO_META/HON_META 사용. ──
export function labelForRole(code: string): string {
    return ROLE_META[code]?.label ?? code;
}
export function labelForRarity(code: string): string {
    return RARITY_META[code]?.label ?? code;
}
export function labelForType(code: string): string {
    return TYPE_META[code]?.label ?? code;
}
export function labelForScope(code: string): string {
    return SCOPE_META[code]?.label ?? code;
}
export function labelForRangeMode(code: string): string {
    return RANGE_MODE_META[code]?.label ?? code;
}
export function labelForDamageType(code: string): string {
    return DAMAGE_TYPE_META[code]?.label ?? code;
}
export function labelForEffectKind(code: string): string {
    return EFFECT_KIND_META[code]?.label ?? code;
}
export function labelForMark(code: string): string {
    return MARK_META[code]?.label ?? code;
}
export function isEffectKindWired(code: string): boolean {
    return EFFECT_KIND_META[code]?.wired ?? false;
}

/** 엔진에 실제 연동된 effectKind 의 Set. */
export const WIRED_EFFECT_KINDS: ReadonlySet<string> = new Set(
    Object.entries(EFFECT_KIND_META).filter(([, m]) => m.wired).map(([k]) => k),
);

// ═════════════════════════════════════════════════════════════════════════════
// 레벨 시스템 헬퍼
// ═════════════════════════════════════════════════════════════════════════════

/** 누적 액션 수로 현재 Lv 계산. 0회면 Lv 0 (미선택). 1회 이상이면 Lv 1 부터. */
export function levelFromActions(actions: number): number {
    if (actions <= 0) return 0;
    // Lv 1 = 선택 시 즉시. 1회 이상이면 이미 1Lv.
    let lv = 1;
    for (let i = 1; i < LEVEL_CURVE_CUMULATIVE.length; i += 1) {
        if (actions >= LEVEL_CURVE_CUMULATIVE[i]) lv = i + 1;
        else break;
    }
    return lv;
}

/** 다음 Lv 까지 남은 액션 수. 이미 마스터면 0. */
export function actionsToNextLevel(actions: number): number {
    const lv = levelFromActions(actions);
    if (lv >= MASTERY_LEVEL) return 0;
    return LEVEL_CURVE_CUMULATIVE[lv] - actions;
}

/** 특정 Lv 도달 시 해금되는 보상 서술. */
export function rewardForLevel(level: number): LevelReward | null {
    if (level < 1 || level > MASTERY_LEVEL) return null;
    return LEVEL_REWARD_TEMPLATE[level - 1] ?? null;
}
